import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Image,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import {
  type SettingMeta,
  type TemplateDescriptionMap,
  type TemplateImageMap,
} from "@/pages/constructor-page/components/settings-tab/types";
import { TooltipStyled } from "@/components/atoms";
import { QuestionIcon } from "@/components/icons";

type Props = {
  meta?: Partial<Pick<SettingMeta, "description" | "image" | "label" | "key">>;
  templateName?: string;
};

export const FieldHint = React.memo(function FieldHint(props: Props) {
  const { t, i18n } = useTranslation();
  if (!props.meta || (!props.meta.image && !props.meta.description)) {
    return null;
  }

  const resolvedImage = props.meta.image
    ? resolveImage(props.meta.image, props.templateName)
    : undefined;

  const templateKey =
    props.templateName &&
    i18n.exists(`dynamic.templatesSettings.${props.meta.key}.description.${props.templateName}`)
      ? props.templateName
      : "_default";

  const resolvedDescription = resolveDescription(props.meta.description, props.templateName);

  const description = !resolvedDescription
    ? resolvedDescription
    : t(`dynamic.templatesSettings.${props.meta.key}.description.${templateKey}`, {
        defaultValue: resolvedDescription,
      });

  if (resolvedImage) {
    return (
      <FieldHintWithDrawer meta={props.meta} imageUrl={resolvedImage} description={description} />
    );
  }

  return (
    <TooltipStyled tooltipText={description} icon>
      <QuestionIcon size="m" />
    </TooltipStyled>
  );
});

const FieldHintWithDrawer = React.memo(function FieldHintWithDrawer({
  meta,
  imageUrl,
  description,
}: {
  meta?: Props["meta"];
  imageUrl: ResolvedImage[];
  description?: string;
}) {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [activeImage, setActiveImage] = React.useState<string>();
  const [lightBoxOpen, setLightboxOpen] = React.useState(false);

  return (
    <>
      <TooltipStyled
        tooltipText={`${description}\n\n${t("FieldHintWithDrawer.tooltip.clickToSeeMore", { defaultValue: "Кликните, чтобы увидеть пример" })}`}
        pre
        icon
        customContent
      >
        <button onClick={onOpen}>
          <QuestionIcon size="m" />
        </button>
      </TooltipStyled>
      <Drawer
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        size="md"
        returnFocusOnClose={false}
      >
        <DrawerOverlay />
        <DrawerContent _dark={{ bg: "gray.800" }}>
          <DrawerCloseButton />
          <DrawerHeader>{meta?.label}</DrawerHeader>
          <DrawerBody>
            <Text mb={4}>{description}</Text>
            <Flex flexDir={"column"} gap={3}>
              {imageUrl.map((image) => (
                <React.Fragment key={image.img}>
                  <Image
                    src={image.img}
                    alt={meta?.label}
                    objectFit="contain"
                    onClick={() => {
                      setActiveImage(image.img);
                      setLightboxOpen(true);
                    }}
                    cursor={"pointer"}
                    border={"1px solid #0000001a"}
                    boxShadow={"0 2px 8px #00000014"}
                  />
                  {image.caption && (
                    <Text textAlign={"center"} variant={"text_xs__normal"} mb={4}>
                      {image.caption}
                    </Text>
                  )}
                </React.Fragment>
              ))}
            </Flex>

            <Lightbox
              open={lightBoxOpen}
              close={() => {
                setActiveImage(undefined);
                setLightboxOpen(false);
              }}
              plugins={[Zoom]}
              slides={[
                {
                  src: activeImage ?? "",
                  alt: meta?.label,
                },
              ]}
              zoom={{
                scrollToZoom: true,
                maxZoomPixelRatio: 5,
              }}
              carousel={{
                finite: true,
              }}
              controller={{ closeOnBackdropClick: true }}
              render={{
                buttonPrev: () => null,
                buttonNext: () => null,
              }}
            />
          </DrawerBody>
          {/* {meta?.key && (
            <DrawerFooter>
              {t("fieldHintWithDrawer.footer.text", { defaultValue: "Используется в шаблонах:" })}{" "}
              {getTemplatesForSettingKey(meta.key).join(", ")}
            </DrawerFooter>
          )} */}
        </DrawerContent>
      </Drawer>
    </>
  );
});

function resolveDescription(
  value?: string | TemplateDescriptionMap,
  templateName?: string,
): string | undefined {
  if (!value) {
    return undefined;
  }

  if (typeof value === "string") {
    return value;
  }

  return value[templateName ?? ""] ?? value._default;
}

type ResolvedImage = {
  img: string;
  caption?: string;
};

function resolveImage(
  meta?: string | TemplateImageMap,
  templateName?: string,
): ResolvedImage[] | undefined {
  if (!meta) {
    return undefined;
  }

  if (typeof meta === "string") {
    return [{ img: meta }];
  }

  let metaData = meta._default;

  if (templateName) {
    metaData = meta[templateName];
  }

  if (Array.isArray(metaData)) {
    if (metaData.every((val) => typeof val === "string")) {
      return metaData.map((val) => ({ img: val }));
    }
    return metaData.map((val) => {
      if (typeof val === "string") {
        return { img: val };
      }
      return {
        img: val.img,
        caption: val.caption,
      };
    });
  }

  const resolved = typeof meta === "string" ? meta : (meta[templateName ?? ""] ?? meta._default);

  if (typeof resolved === "string") {
    return [{ img: resolved }];
  }

  if (Array.isArray(resolved) && resolved.every((item) => typeof item === "string")) {
    return resolved.map((img) => ({ img }));
  }

  return resolved;
}
